/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Service d'analyse juridique assistée par Intelligence Artificielle.
 * Communique exclusivement avec le backend Docarn Juridique (/api/ai/analyze-case).
 */

import apiClient from '../Api/ApiClient';
import { AiCaseAnalysisRequest, AiCaseAnalysisResponse } from '../Types/ai';

class AiSearchService {
  /**
   * Soumet une question juridique à l'orchestrateur IA du backend.
   * Supporte l'annulation via AbortSignal.
   */
  async analyzeLegalCase(
    request: AiCaseAnalysisRequest,
    signal?: AbortSignal
  ): Promise<AiCaseAnalysisResponse> {
    try {
      const response = await apiClient.post<AiCaseAnalysisResponse>(
        '/api/ai/analyze-case',
        request,
        { signal }
      );
      return response;
    } catch (error: any) {
      // Si la requête a été volontairement annulée par l'utilisateur
      if (error?.name === 'AbortError' || signal?.aborted) {
        throw error;
      }

      // Si le backend n'a pas encore déployé l'endpoint /api/ai/analyze-case (ex: 404 ou 502)
      // Adaptateur de développement explicite pour prévisualiser l'architecture
      const isEndpointMissing =
        error?.message?.includes('404') ||
        error?.status === 404 ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkError');

      if (isEndpointMissing && process.env.NODE_ENV !== 'production') {
        console.warn(
          "[DOCARN IA] L'endpoint backend /api/ai/analyze-case n'est pas encore déployé. Utilisation de l'adaptateur de prévisualisation de développement."
        );
        return this.getDevelopmentSimulation(request.question);
      }

      throw error;
    }
  }

  /**
   * Adaptateur de développement local :
   * Fournit une réponse structurée de démonstration clairement étiquetée comme simulation.
   */
  private async getDevelopmentSimulation(question: string): Promise<AiCaseAnalysisResponse> {
    // Simuler un léger délai d'inférence de 1.8s
    await new Promise((resolve) => setTimeout(resolve, 1800));

    const isBail = /bail|propriétaire|caution|loyer|logement|locataire/i.test(question);
    const isTravail = /travail|licenciement|salaire|employeur|salarié|contrat/i.test(question);

    if (isTravail) {
      return {
        requestId: `DOC-IA-DEV-${Date.now()}`,
        status: 'COMPLETED',
        summary:
          "Analyse relative à la rupture du contrat de travail et aux droits indemnitaires du salarié dans le cadre du droit social camerounais.",
        legalQualifications: [
          'Droit du travail',
          'Rupture du contrat de travail',
          'Licenciement & Préavis',
          'Indemnités de rupture'
        ],
        legalReferences: [
          {
            documentId: 1,
            textNature: 'LOI',
            referenceNumber: 'Loi N° 92/007 du 14 août 1992',
            officialTitle: 'Portant Code du Travail de la République du Cameroun',
            articleNumber: 'Article 34',
            articleExcerpt:
              'Le contrat de travail à durée indéterminée peut toujours cesser par la volonté de l\'une des parties sous réserve du préavis réglementaire.',
            relevanceExplanation:
              'Définit les conditions légales de notification et la durée obligatoire du préavis de rupture.',
            legalStatus: 'EN_VIGUEUR',
            isVerifiedSource: true
          },
          {
            documentId: 1,
            textNature: 'LOI',
            referenceNumber: 'Loi N° 92/007 du 14 août 1992',
            officialTitle: 'Portant Code du Travail de la République du Cameroun',
            articleNumber: 'Article 37',
            articleExcerpt:
              'Tout licenciement abusif donne lieu à des dommages-intérêts fixés par la juridiction compétente.',
            relevanceExplanation:
              'Base légale de la contestation du motif de licenciement et du calcul des réparations.',
            legalStatus: 'EN_VIGUEUR',
            isVerifiedSource: true
          }
        ],
        legalAnalysis: {
          applicableRules:
            "En droit du travail camerounais (Loi N° 92/007), toute rupture unilatérale du contrat à durée indéterminée doit reposer sur un motif légitime (faute lourde, inaptitude, motif économique) et faire l'objet d'un préavis écrit ou d'une indemnité compensatrice équivalente.",
          caseApplication:
            "Au vu de votre description, la procédure de rupture doit être confrontée aux exigences d'audition préalable et de motivation formelle. À défaut de motif réel et sérieux prouvé par l'employeur, le licenciement est qualifié d'abusif ouvrant droit au solde de tout compte et à des dommages-intérêts.",
          limitationsAndCaveats:
            "L'ancienneté précise du salarié, la convention collective de branche applicable et la tenue d'une éventuelle lettre de licenciement écrite sont des éléments indispensables à vérifier pour chiffrer précisément les droits.",
          conclusion:
            "Votre position juridique permet d'exiger le versement immédiat des indemnités légales de rupture et, en cas de contestation du motif, la saisine de l'Inspection du Travail pour tentative de conciliation."
        },
        recommendedActions: [
          "Réunir le contrat de travail initial, les bulletins de paie des 12 derniers mois et la notification de licenciement.",
          "Adresser un courrier de mise en demeure réclamant le solde de tout compte sous 8 jours ouvrés.",
          "Saisir l'Inspecteur du Travail et de la Prévoyance Sociale du ressort territorial pour une tentative de conciliation préalable.",
          "Consulter un avocat ou un conseil juridique si aucun accord amiable n'est trouvé en inspection."
        ],
        relatedDocuments: [
          {
            id: 1,
            nature: 'LOI',
            referenceNumber: 'Loi N° 92/007',
            officialTitle: 'Code du Travail du Cameroun',
            effectiveYear: 1992,
            legalStatus: 'EN_VIGUEUR',
            areaOfLaw: 'DROIT_TRAVAIL',
            issuingAuthority: 'Présidence de la République'
          }
        ],
        disclaimer:
          "Cette analyse générée par l'Assistant IA est fournie à titre indicatif et ne remplace pas une consultation formelle auprès d'un professionnel du droit habilité.",
        isDevelopmentSimulation: true,
        processingTimeMs: 1840
      };
    }

    // Cas standard (Baux / Droit Civil général)
    return {
      requestId: `DOC-IA-DEV-${Date.now()}`,
      status: 'COMPLETED',
      summary:
        "Analyse de la contestation relative à la retenue du dépôt de garantie (caution) par le bailleur à l'issue de la libération des lieux sans dégradation constatée.",
      legalQualifications: [
        'Droit civil des obligations',
        'Baux d\'habitation & commerciaux',
        'Restitution du dépôt de garantie',
        'Contrat de louage de choses'
      ],
      legalReferences: [
        {
          documentId: 2,
          textNature: 'CODE',
          referenceNumber: 'Code Civil applicable au Cameroun',
          officialTitle: 'Dispositions régissant le contrat de louage',
          articleNumber: 'Article 1730 & suivants',
          articleExcerpt:
            'S\'il a été fait un état des lieux entre le bailleur et le preneur, celui-ci doit rendre la chose telle qu\'il l\'a reçue, excepté ce qui a péri ou a été dégradé par vétusté ou force majeure.',
          relevanceExplanation:
            'Consacre l\'absence de responsabilité du locataire dès lors qu\'aucune dégradation n\'est contradictoirement constatée lors de la remise des clés.',
          legalStatus: 'EN_VIGUEUR',
          isVerifiedSource: true
        },
        {
          documentId: 3,
          textNature: 'DECRET',
          referenceNumber: 'Réglementation des baux urbains',
          officialTitle: 'Dispositions d\'encadrement des rapports entre bailleurs et locataires',
          articleNumber: 'Dispositions relatives au cautionnement',
          articleExcerpt:
            'Le dépôt de garantie a pour objet exclusif de garantir les obligations locatives impayées et les dégradations imputables au preneur.',
          relevanceExplanation:
            'Interdit toute rétention unilatérale du dépôt en l\'absence de justificatifs de dépenses ou de loyers impayés.',
          legalStatus: 'EN_VIGUEUR',
          isVerifiedSource: true
        }
      ],
      legalAnalysis: {
        applicableRules:
          "En matière de bail d'habitation, le dépôt de garantie n'est pas la propriété définitive du bailleur. Il constitue un cautionnement fiduciaire affecté à la couverture d'éventuels manquements contractuels (dettes locatives ou dégradations constatées contradictoirement dans un état des lieux de sortie).",
        caseApplication:
          "Si le logement a été libéré sans impayé et que l'état des lieux de sortie ne mentionne aucune dégradation imputable au locataire, la rétention du dépôt de garantie est dépourvue de cause légitime et constitue une inexécution contractuelle.",
        limitationsAndCaveats:
          "Il convient de vérifier les stipulations expresses du contrat de bail signé, l'existence d'un état des lieux d'entrée et de sortie dûment paraphé, ainsi que la notification formelle du congé (préavis).",
        conclusion:
          "Le bailleur est légalement tenu de restituer l'intégralité du dépôt de garantie dans le délai convenu. À défaut, des intérêts moratoires ou dommages-intérêts pour résistance abusive peuvent être réclamés."
      },
      recommendedActions: [
        "Vérifier la détention des originaux de l'état des lieux d'entrée et de sortie signés par les deux parties.",
        "Adresser au bailleur une mise en demeure formelle par lettre recommandée avec accusé de réception ou exploit d'huissier lui accordant un délai de 8 à 15 jours pour restituer la caution.",
        "À défaut de restitution, engager une procédure d'injonction de payer devant la juridiction civile compétente.",
        "Consulter les textes officiels ci-dessous pour étayer votre mise en demeure écrite."
      ],
      relatedDocuments: [
        {
          id: 2,
          nature: 'CODE',
          referenceNumber: 'Code Civil',
          officialTitle: 'Code Civil - Livre III : Des manières dont on acquiert la propriété (Du contrat de louage)',
          effectiveYear: 1960,
          legalStatus: 'EN_VIGUEUR',
          areaOfLaw: 'DROIT_CIVIL',
          issuingAuthority: 'République du Cameroun'
        }
      ],
      disclaimer:
        "Cette analyse synthétique générée par l'Assistant IA est fournie à titre indicatif et ne constitue pas une consultation d'avocat au barreau.",
      isDevelopmentSimulation: true,
      processingTimeMs: 1800
    };
  }
}

export const aiSearchService = new AiSearchService();
export default aiSearchService;
